import EvaluateProcessor from "../../core/internal/evaluation/EvaluateProcessor"
import { EvaluationMode } from "../../public/model"
import ObjectUtil from "../../core/internal/util/ObjectUtil"
import WorkspaceConfigFetcher from "../../core/internal/workspace/config/WorkspaceConfigFetcher"
import WorkspaceEvaluationFetcher from "../../core/internal/workspace/evaluation/WorkspaceEvaluationFetcher"
import WorkspaceEvaluationManager from "../../core/internal/workspace/evaluation/WorkspaceEvaluationManager"

import { InAppMessageEventMatcher } from "../iam/trigger/InAppMessageEventMatcher"
import { InAppMessageTriggerDeterminer } from "../iam/trigger/InAppMessageTriggerDeterminer"
import { LocalInAppMessageTriggerDeterminer } from "../iam/trigger/LocalInAppMessageTriggerDeterminer"
import { RemoteInAppMessageTriggerDeterminer } from "../iam/trigger/RemoteInAppMessageTriggerDeterminer"
import { InAppMessageDeliverEvaluator } from "../iam/deliver/evaluator/InAppMessageDeliverEvaluator"
import { InAppMessageDeliverLocalEvaluator } from "../iam/deliver/evaluator/InAppMessageDeliverLocalEvaluator"
import { InAppMessageDeliverRemoteEvaluator } from "../iam/deliver/evaluator/InAppMessageDeliverRemoteEvaluator"

/**
 * The evaluation-mode-specific in-app message components: the trigger determiner and deliver
 * evaluator whose wiring differs between local and remote evaluation. The evaluators themselves
 * are registered by HackleCore.create; everything downstream (trigger/deliver processors,
 * scheduling, present) consumes these via their interfaces and stays mode-agnostic.
 */
export interface InAppMessageModeComponents {
  triggerDeterminer: InAppMessageTriggerDeterminer
  deliverEvaluator: InAppMessageDeliverEvaluator
}

/** The shared bootstrap locals the in-app message builders draw from. */
export interface InAppMessageModeDeps {
  evaluateProcessor: EvaluateProcessor
  eventMatcher: InAppMessageEventMatcher
  workspaceConfigFetcher: WorkspaceConfigFetcher
  workspaceEvaluationFetcher: WorkspaceEvaluationFetcher
  /** Entity re-evaluation transport for deliver-time re-resolution; null in local mode. */
  workspaceEvaluationManager: WorkspaceEvaluationManager | null
}

/** Builds the in-app message mode components for the given mode. Unknown modes degrade to local. */
export function buildInAppMessageModeComponents(
  mode: EvaluationMode,
  deps: InAppMessageModeDeps
): InAppMessageModeComponents {
  switch (mode) {
    case "remote":
      return buildRemoteInAppMessageModeComponents(deps)
    case "local":
    default:
      return buildLocalInAppMessageModeComponents(deps)
  }
}

function buildLocalInAppMessageModeComponents(deps: InAppMessageModeDeps): InAppMessageModeComponents {
  return {
    triggerDeterminer: new LocalInAppMessageTriggerDeterminer(
      deps.eventMatcher,
      deps.workspaceConfigFetcher,
      deps.evaluateProcessor
    ),
    deliverEvaluator: new InAppMessageDeliverLocalEvaluator(deps.workspaceConfigFetcher, deps.evaluateProcessor)
  }
}

function buildRemoteInAppMessageModeComponents(deps: InAppMessageModeDeps): InAppMessageModeComponents {
  const workspaceEvaluationManager = ObjectUtil.requiredNotNullOrUndefined(
    deps.workspaceEvaluationManager,
    () => "workspaceEvaluationManager is required in remote evaluation mode"
  )

  return {
    triggerDeterminer: new RemoteInAppMessageTriggerDeterminer(
      deps.eventMatcher,
      deps.workspaceEvaluationFetcher,
      deps.evaluateProcessor
    ),
    deliverEvaluator: new InAppMessageDeliverRemoteEvaluator(workspaceEvaluationManager, deps.evaluateProcessor)
  }
}
